import { FC } from 'react';
import { Paragraph, Card, Link } from 'roo-ui/components';
import { Formik, Form, useFormikContext, FormikConfig } from 'formik';
import { schema } from './schema';
import { useScrollTopOnMount, useStayDateRange } from 'lib/hooks';
import { TwoColumnWithBookingSummary } from 'components/Layout';
import LoadingBlock from 'components/LoadingBlock';
import { ContentHeading } from 'components/Typography';
import ButtonGroup from 'components/ButtonGroup';
import { useSubmitEnabled } from './lib/useSubmitEnabled';
import { StayDates } from 'types';
import { useDoDatesContain } from './lib/useDoDatesContain';
import NoAvailabilityAlert from './NoAvailabilityAlert';
import * as track from 'lib/gaTracking/changeDatesPageEvents';
import { DateRangeInput } from 'components/Form';
import { toCalendarDateFormat, toDisplayDateOnlyFormat } from 'lib/utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { DateRange } from 'components/Form/DateRangeInput';
import { openLiveChat } from 'lib/liveChat/actions';

type DatesFormProps = FormikConfig<StayDates> & InnerDatesFormProps;

interface InnerDatesFormProps {
  originalValues: StayDates;
  unavailableValues?: StayDates;
  loading: boolean;
  minDate: Date;
  maxDate: Date;
  blockedDates: Date[];
  onClickBack: Function;
}

const DatesForm: FC<DatesFormProps> = ({ initialValues, onSubmit, ...rest }) => (
  <Formik<StayDates> initialValues={initialValues} onSubmit={onSubmit} validationSchema={schema} validateOnMount>
    <InnerDatesForm {...rest} />
  </Formik>
);

const InnerDatesForm = ({
  originalValues,
  unavailableValues,
  blockedDates,
  minDate,
  maxDate,
  onClickBack,
  loading,
}: InnerDatesFormProps) => {
  useScrollTopOnMount();
  const { values, isValid, handleSubmit, setFieldValue } = useFormikContext<StayDates>();
  const { maxCheckOutDate } = useStayDateRange({ checkIn: values.checkIn });

  const submitEnabled = useSubmitEnabled({
    isValid,
    loading,
    dates: values,
    originalDates: originalValues,
    unavailableDates: unavailableValues,
  });

  const isUnavaliable = useDoDatesContain(values, unavailableValues);

  return (
    <Form>
      <TwoColumnWithBookingSummary heading="Change dates" showSummaryOnMobile>
        {loading && <LoadingBlock />}
        {!loading && (
          <>
            <Paragraph fontSize="lg" pb={[2, 3]}>
              Enter your new stay dates below. Please note payment adjustments may apply.
            </Paragraph>

            <Paragraph fontSize="lg" pt={[2, 3]} pb={[7, 8]} data-testid="insufficient-availability-message">
              If there is no availability for your room then please{' '}
              <Link onClick={openLiveChat} underline>
                contact us
              </Link>{' '}
              to see if there is availability for a different room.
            </Paragraph>

            <ContentHeading>New stay dates</ContentHeading>
            {isUnavaliable && <NoAvailabilityAlert />}
            <Card display="flex" p={[5, 6]} mb={[5, 8]}>
              <input type="hidden" name="checkIn" disabled={loading} />
              <input type="hidden" name="checkOut" disabled={loading} />

              <DateRangeInput
                width={['100%', '80%']}
                startDate={parseISO(values.checkIn)}
                endDate={parseISO(values.checkOut)}
                blockedDates={blockedDates}
                minDate={minDate}
                maxDate={maxDate}
                isOpen={true}
                onChange={(stayDates: DateRange) => {
                  if (stayDates.startDate) {
                    setFieldValue('checkIn', toCalendarDateFormat(stayDates.startDate));
                  }

                  if (stayDates.endDate) {
                    if (maxCheckOutDate && differenceInCalendarDays(stayDates.endDate, maxDate) > 0) {
                      setFieldValue('checkOut', toCalendarDateFormat(maxDate));
                    } else {
                      setFieldValue('checkOut', toCalendarDateFormat(stayDates.endDate));
                    }
                  }

                  track.stayDateRange(
                    `${stayDates.startDate ? toDisplayDateOnlyFormat(stayDates.startDate) : null} - ${
                      stayDates.endDate ? toDisplayDateOnlyFormat(stayDates.endDate) : null
                    }`,
                  );
                }}
              />
            </Card>
            <ButtonGroup
              withBackIcon={false}
              desktopBackLabel="Cancel"
              mobileBackLabel="Cancel"
              onClickBack={onClickBack}
              continueLabel="Next"
              onClickContinue={handleSubmit}
              CustomContinueButton={null}
              continueButtonProps={{
                type: 'submit',
                disabled: !submitEnabled,
                loading: loading,
                width: ['100%', '224px'],
              }}
            />
          </>
        )}
      </TwoColumnWithBookingSummary>
    </Form>
  );
};

export default DatesForm;
